// SPDX-License-Identifier: BUSL-1.1

/**
 * Reads a feature flag from build.ceo's own /api/flags endpoint.
 * Returns false if the flag is unknown, disabled, or the endpoint is
 * unreachable (graceful degradation — never crashes the UI).
 *
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import type { Flag } from '@/types/api';

/**
 * Read URL query param override for a flag.
 * `?ff_dashboard-v2=true` forces the flag ON without touching the database.
 * Used for deterministic Playwright runs and PR preview deployments.
 * Returns `undefined` when no override is present so the hook falls through
 * to the normal React Query path.
 */
function readUrlOverride(key: string): boolean | undefined {
  if (typeof window === 'undefined') return undefined;
  const override = new URLSearchParams(window.location.search).get(`ff_${key}`);
  if (override === 'true') return true;
  if (override === 'false') return false;
  return undefined;
}

export function useFeatureFlag(key: string): boolean {
  // ALWAYS call useQuery — hooks must run in the same order on every render.
  // The URL override takes precedence over the result but does not skip the hook.
  const { data } = useQuery({
    queryKey: ['flags'],
    queryFn: () => apiFetch<Flag[]>('/flags'),
    staleTime: 60_000,
    // Never throw — missing /flags endpoint should silently fall back to OFF
    retry: 1,
  });

  const urlOverride = readUrlOverride(key);
  if (urlOverride !== undefined) return urlOverride;

  return data?.find((f) => f.key === key)?.enabled ?? false;
}
